/* eslint-disable eslint-comments/no-unlimited-disable */
/* eslint-disable */
import * as React from "react";
import { Form } from "semantic-ui-react";
import MaskedInput from "react-text-mask";
import * as moment from "moment";
import { validate } from "not-valid";
import { isEqual } from "lodash";

import { QuestionProps, AnsweredQuestion, QuestionAnswer } from "../../model";
import { isQuestionDisabled } from "../../questionnaireConfig/models";

import { QuestionWrapper } from "./QuestionWrapper";

interface DateQuestionState {
    inputDate: string;
    showError: boolean;
    error: string;
    warningMessage: string;
    isFocused: boolean;
}

const dateInputRegex = /\d{2}\/\d{2}\/\d{4}/;
const displayFormat = "DD/MM/YYYY";
const outputFormat = "YYYY-MM-DD";

// Value returned by Moment.js in case of invalid date
export const invalidDateAnswer = "Invalid date";

/**
 *  @deprecated
 *  Prefer DateQuestion unless you're rendering where the questionnaire context is not available. e.g. OldProgressBarQuestionnaireContainer
*/
class OldDateQuestion extends React.Component<QuestionProps, DateQuestionState> {
    constructor(props: QuestionProps) {
        super(props);
        this.state = {
            inputDate: null,
            showError: false,
            error: null,
            warningMessage: null,
            isFocused: false
        };
    }

    public componentDidMount() {
        const inputDate = this.getInputDateAsString(this.props.answer);
        this.setState({
            inputDate,
            showError: false,
            error: null,
            warningMessage: null,
            isFocused: false
        });

        this.validateAndUpdate(inputDate);
    }

    public componentDidUpdate(prevProps: QuestionProps) {
        if (!isEqual(prevProps.answer, this.props.answer)) {
            if (!this.state.isFocused) {
                const inputDate = this.getInputDateAsString(this.props.answer);
                if (inputDate) {
                    this.validateAndUpdate(inputDate);
                }
            }
        }
        if (!isEqual(prevProps.mandatory, this.props.mandatory)) {
            const inputDate = this.getInputDateAsString(this.props.answer);
            if (inputDate) {
                this.validateAndUpdate(inputDate);
            }
        }
    }

    public render() {
        if (isQuestionDisabled(this.props.questionConfig)) {
            return null;
        }

        const showError = this.state.showError || this.props.showError;
        const error = showError ? this.state.error : null;
        return (
            <QuestionWrapper
                inline={this.props.inline}
                question={this.props.questionConfig.questionText}
                mandatory={this.props.mandatory}
                hideDivider={this.props.hideDivider}
                questionClassName={this.props.questionClassName}
                error={(showError && this.props.mandatory && !this.props.answer && "This field must be completed" || error)}
                readonly={this.props.readonly}
                removable={{
                    ...this.props.removable,
                    questionId: this.props.questionConfig.id,
                    isRemovedByUser: this.props.isRemovedByUser
                }}
                outdated={{
                    ...this.props.outdated,
                    questionId: this.props.questionConfig.id
                }}
                warningMessage={this.state.warningMessage}
            >
                <Form.Field error={!!error}>
                    <div className="ui input">
                        {this.renderValue()}
                    </div>
                </Form.Field>
            </QuestionWrapper>
        );
    }

    private renderValue() {
        if (this.props.readonly) {
            const answer = this.props.answer && moment(this.props.answer as string, outputFormat);
            return <div>{answer && answer.isValid() && answer.format(displayFormat)}</div>;
        }
        return (
            <MaskedInput
                type="tel"
                value={this.state.inputDate}
                onChange={this.onChange}
                onBlur={this.onBlur}
                onFocus={this.onFocus}
                keepCharPositions={true}
                placeholder={displayFormat}
                guide={false}
                mask={[/\d/, /\d/, "/", /\d/, /\d/, "/", /\d/, /\d/, /\d/, /\d/]}
            />
        );
    }

    private onChange = (event: React.FormEvent<HTMLInputElement>) => {
        const inputDate = event.currentTarget.value;
        this.validateAndUpdate(inputDate);
    };

    private onBlur = () => {
        this.setState({ showError: true, isFocused: false });
    };

    private onFocus = () => {
        this.setState({ showError: true, isFocused: true });
    };

    private validateAndUpdate = (inputDate: string) => {
        const { questionConfig, updateQuestion } = this.props;
        if (isQuestionDisabled(questionConfig)) {
            return;
        }

        const date = moment(inputDate, displayFormat);
        const isValid = this.isValid(inputDate);
        const error = isValid ? null : "Please enter a valid date";
        if (inputDate === invalidDateAnswer) {
            this.setState({ inputDate: "", error });
        } else {
            this.setState({ inputDate, error });
        }

        const newQuestion: AnsweredQuestion = {
            question: questionConfig.questionText,
            answer: date.format(outputFormat),
            isValid
        };
        updateQuestion(newQuestion);
        if (this.props.onAnswerChange) {
            this.props.onAnswerChange(newQuestion.answer);
        }

        const hasCustomValidators = this.props.validators?.length > 0;
        const hasWarningValidators = this.props.warning?.validators?.length > 0;
        const emptyAndNotMandatory = !this.props.mandatory && this.isAnswerEmpty(date);
        if (isValid && (hasCustomValidators || hasWarningValidators) && !emptyAndNotMandatory) {
            this.props.validateQuestion(this.getValidatedQuestion(questionConfig.questionText, date));
        }
    };

    private isValid(inputDate: string) {
        const date = moment(inputDate, displayFormat);
        const emptyAndNotMandatory = !this.props.mandatory && this.isAnswerEmpty(date);
        const isValidMomentWithProperFormat = date.isValid() && dateInputRegex.test(inputDate);
        const isValid = emptyAndNotMandatory || isValidMomentWithProperFormat;
        return isValid;
    }

    private isAnswerEmpty(date: moment.Moment): boolean {
        return date === null || date === undefined || date.creationData().input === "";
    }

    private getInputDateAsString(answer: QuestionAnswer): string {
        const date = moment(answer as string);
        return answer && date.isValid() ? date.format(displayFormat) : "";
    }

    private async getValidatedQuestion(question: string, answer: moment.Moment): Promise<AnsweredQuestion> {
        const errors = await validate(this.props.validators || [], answer);
        const warningMessages = await validate(this.props.warning?.validators || [], answer);
        this.setState({ error: errors[0], warningMessage: warningMessages[0] });
        const isValid = errors.length === 0 && (!!answer || !this.props.mandatory);
        return { question, answer: answer.format(outputFormat), isValid };
    }
}

export { OldDateQuestion };
